import { BehaviorSubject, firstValueFrom, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { SharedApiV2 } from "@capstone/mock-api";

const DEFAULT_VALUES = {
  pageSize: 50,
  currentPage: 1,
};

export class PaginationV2 {
  public constructor(
    args: PageInfo = DEFAULT_VALUES,
    options?: { preserveCustomValues?: boolean },
  ) {
    // Store the custom pageSize if it differs from default
    if (args.pageSize && args.pageSize !== DEFAULT_VALUES.pageSize) {
      this.customPageSize = args.pageSize;
    }

    this.preserveCustomValues = options?.preserveCustomValues ?? false;

    this.pageSubject.next(
      new Page({
        currentPage: args.currentPage,
        pageSize: args.pageSize,
      }),
    );
  }

  private readonly customPageSize?: number;
  private readonly preserveCustomValues: boolean;

  private readonly requestNotifierSubject = new Subject<number>();
  /**
   * Emits a notification with the page number whenever a request to load a new
   * page is received.
   */
  public readonly requestNotifier = this.requestNotifierSubject.asObservable();

  private readonly pageSubject = new BehaviorSubject<Page>(
    new Page(DEFAULT_VALUES),
  );

  // Number of the page to be requested
  public selectedPageNumber = DEFAULT_VALUES.currentPage;

  public readonly hasPrevious = this.pageSubject.pipe(
    map((page) => page.hasPrevious),
  );

  public readonly hasNext = this.pageSubject.pipe(map((page) => page.hasNext));

  public reset<T>(result?: SharedApiV2.PagedList<T>): void {
    const page = new Page(DEFAULT_VALUES);
    if (result) {
      page.load(result, {
        preserveCustomPageSize: this.preserveCustomValues,
        customPageSize: this.customPageSize,
      });
    }
    this.pageSubject.next(page);
    this.selectedPageNumber = page.currentPage;
  }

  /**
   * Initiate a request to load the previous page of results.
   */
  public async loadPage<T>(result: SharedApiV2.PagedList<T>): Promise<void> {
    const page = await firstValueFrom(this.pageSubject);
    page.load(result, {
      preserveCustomPageSize: this.preserveCustomValues,
      customPageSize: this.customPageSize,
    });
    // Re-emit the page so that the hasPrevious and hasNext observables update.
    this.pageSubject.next(page);
    this.selectedPageNumber = page.currentPage;
  }

  public async requestPrevious(): Promise<void> {
    const pageNumber = (await firstValueFrom(this.pageSubject)).getPrevious();
    if (pageNumber !== undefined) {
      this.selectedPageNumber = pageNumber;
      this.requestNotifierSubject.next(pageNumber);
    }
  }

  /**
   * Initiate a request to load the next page of results.
   */
  public async requestNext(): Promise<void> {
    const pageNumber = (await firstValueFrom(this.pageSubject)).getNext();
    if (pageNumber !== undefined) {
      this.selectedPageNumber = pageNumber;
      this.requestNotifierSubject.next(pageNumber);
    }
  }

  public get currentPageNumber(): number {
    return this.selectedPageNumber;
  }

  public get pageSize(): number {
    return this.pageSubject.getValue().pageSize;
  }
}

export class Page {
  public constructor(args: PageInfo = DEFAULT_VALUES) {
    this.info = args;
  }

  private info?: PageInfo;

  public get hasPrevious(): boolean {
    return !!this.info?.hasPrevious;
  }

  public get hasNext(): boolean {
    return !!this.info?.hasNext;
  }

  public get currentPage(): number {
    return this.info?.currentPage ?? DEFAULT_VALUES.currentPage;
  }

  public get pageSize(): number {
    return this.info?.pageSize ?? DEFAULT_VALUES.pageSize;
  }

  public load<T>(
    result: SharedApiV2.PagedList<T>,
    options?: {
      preserveCustomPageSize?: boolean;
      customPageSize?: number;
    },
  ): void {
    this.info = {
      currentPage: result.currentPage,
      hasNext: result.hasNext,
      hasPrevious: result.hasPrevious,
      // Use custom pageSize if preserving, otherwise use server's pageSize
      pageSize:
        options?.preserveCustomPageSize && options?.customPageSize
          ? options.customPageSize
          : result.pageSize,
    };
  }

  public getPrevious(): number | undefined {
    return this.info?.hasPrevious && this.info?.currentPage
      ? this.info.currentPage - 1
      : undefined;
  }

  public getNext(): number | undefined {
    return this.info?.hasNext && this.info?.currentPage
      ? this.info.currentPage + 1
      : undefined;
  }
}

interface PageInfo {
  currentPage: number;
  pageSize: number;
  hasPrevious?: boolean;
  hasNext?: boolean;
}
